import axios from 'axios';
import {
  getJwkKeystore, jweDecryption, jweEncryption, JWE_KEY,
} from 'Utils/crypt';
import toast from 'Utils/toast';
import history from './history';

const confAxios = {
  baseURL: '/api',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
};

const api = axios.create(confAxios);

const apiBlob = axios.create({
  ...confAxios,
  responseType: 'blob',
  // timeout: 5000,
});

const getToken = () => new Promise((resolve) => {
  resolve(localStorage.getItem('token'));
});

const formatToken = (token) => {
  if (!token) return undefined;
  if (token.startsWith('Bearer ')) return token;
  return `Bearer ${token}`;
};

export const getResponseData = async (response, key, useJWE = true) => {
  // Skip decryption if JWE is disabled or data is null/empty
  if ([null, '', undefined].includes(response?.data) || !useJWE) {
    return response?.data;
  }

  // Skip decryption if response.data is already a plain object (not a JWE string)
  if (typeof response?.data === 'object' && response?.data !== null) {
    return response?.data;
  }

  // Only decrypt if data is a string (JWE format)
  if (typeof response?.data === 'string') {
    const data = await jweDecryption(response?.data, key);
    return data;
  }

  // Fallback: return data as-is
  return response?.data;
};

const checkNotAuthored = async (err) => {
  if (err.response?.status === 401 || err.message === 'no key found') {
    const reason = err.response?.status === 401
      ? `401 from ${err.config?.url}`
      : `JWE key mismatch — ${err.message}`;
    console.warn('[AUTH] forcing logout:', reason);
    localStorage.removeItem('token');
    localStorage.removeItem(JWE_KEY);
    toast.info('Votre session a expiré, veuillez vous reconnecter.', { toastId: 'session-expired' });
    history.push('/login');
    const sentinelErr = new Error('handled');
    sentinelErr.handled = true;
    return Promise.reject(sentinelErr);
  }
  if (err.response?.data instanceof Blob) {
    let text;
    try { text = await err.response.data.text(); } catch (_) { /* ignore read errors */ }
    if (text) {
      let payload = text;
      try {
        const key = await getJwkKeystore();
        payload = await jweDecryption(text, key);
      } catch (_) { /* not JWE-encrypted, use raw text */ }
      let parsed;
      if (typeof payload === 'object' && payload !== null) {
        parsed = payload;
      } else {
        try { parsed = JSON.parse(payload); } catch (_) { /* not valid JSON */ }
      }
      if (parsed) {
        const enriched = new Error(err.message);
        enriched.response = { ...err.response, data: parsed };
        enriched.config = err.config;
        throw enriched;
      }
    }
  }
  if (typeof err.response?.data === 'string' && err.response.data.length > 0) {
    let decrypted;
    try {
      const key = await getJwkKeystore();
      decrypted = await jweDecryption(err.response.data, key);
    } catch (_) { /* leave as-is if decryption fails */ }
    if (decrypted) {
      const enriched = new Error(err.message);
      enriched.response = { ...err.response, data: decrypted };
      enriched.config = err.config;
      throw enriched;
    }
  }
  throw err;
};

export const secureGet = async (url, config = {}, params = {}, useJWE = true) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  const { headers, ...restConfig } = config;
  const conf = {
    headers: {
      ...headers,
      Authorization: formatToken(token),
    },
    ...restConfig,
    params,
  };
  return api
    .get(`${url}`, conf)
    .then(async (response) => {
      const data = await getResponseData(response, key, useJWE);
      return { ...response, data };
    })
    .catch(checkNotAuthored);
};

export const secureDelete = async (url) => {
  const token = await getToken();
  return api
    .delete(`${url}`, {
      headers: { Authorization: formatToken(token) },
    })
    .catch(checkNotAuthored);
};

export const securePost = async (url, data, config = {}, useJWE = true) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  const { headers, ...restConfig } = config;
  const dataJWE = useJWE && data ? await jweEncryption(data, key) : data;
  const conf = {
    headers: {
      ...headers,
      Authorization: formatToken(token),
    },
    ...restConfig,
  };
  return api
    .post(`${url}`, dataJWE, conf)
    .then(async (response) => {
      if (response.headers['content-type']?.search('application/pdf') !== -1) {
        return response;
      }
      const result = await getResponseData(response, key, useJWE);
      return { ...response, data: result };
    })
    .catch(checkNotAuthored);
};

export const securePostPdf = async (url, data, config = {}) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  const { headers, ...restConfig } = config;
  const dataJWE = await jweEncryption(data, key);
  const conf = {
    headers: {
      ...headers,
      Authorization: formatToken(token),
    },
    ...restConfig,
  };
  return apiBlob
    .post(`${url}`, dataJWE, conf)
    .then(async (response) => response)
    .catch(checkNotAuthored);
};

export const secureGetPdf = async (url, config = {}) => {
  const token = await getToken();
  const { headers, ...restConfig } = config;
  const conf = {
    headers: {
      ...headers,
      Authorization: formatToken(token),
    },
    ...restConfig,
  };
  return apiBlob
    .get(`${url}`, conf)
    .then(async (response) => response)
    .catch(checkNotAuthored);
};

export const securePatch = async (url, data) => {
  const token = await getToken();
  return api
    .patch(`${url}`, data, {
      headers: { Authorization: formatToken(token) },
    })
    .catch(checkNotAuthored);
};

export const securePut = async (url, data) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  const dataJWE = data ? await jweEncryption(data, key) : data;
  return api
    .put(`${url}`, dataJWE, {
      headers: { Authorization: formatToken(token) },
    })
    .then(async (response) => {
      const result = await getResponseData(response, key);
      return { ...response, data: result };
    })
    .catch(checkNotAuthored);
};

export default api;
